import { z } from "zod";
import type { OrgPlan } from "../common/org-plan";

export const AGENT_MEDIA_ACTORS = ["aiAssistant", "systemAgent"] as const;
export const AGENT_MEDIA_KINDS = [
	"image",
	"audio",
	"video",
	"pdf",
	// 'document' is the merged "Documents & files" family: spreadsheets, Word/
	// PowerPoint/OpenDocument, plain text, CSV, JSON, and any other file type.
	"document",
] as const;
export const AGENT_MEDIA_ACTIONS = ["receive", "read", "send"] as const;

export const AgentMediaActorSchema = z.enum(AGENT_MEDIA_ACTORS);
export const AgentMediaKindSchema = z.enum(AGENT_MEDIA_KINDS);
export const AgentMediaActionSchema = z.enum(AGENT_MEDIA_ACTIONS);
export type AgentMediaActor = z.infer<typeof AgentMediaActorSchema>;
export type AgentMediaKind = z.infer<typeof AgentMediaKindSchema>;
export type AgentMediaAction = z.infer<typeof AgentMediaActionSchema>;

const CapabilitySetSchema = z.object({
	receive: z.boolean(),
	read: z.boolean(),
	send: z.boolean(),
});
const ActorCapabilitiesSchema = z.object(
	Object.fromEntries(
		AGENT_MEDIA_KINDS.map((kind) => [kind, CapabilitySetSchema])
	) as Record<AgentMediaKind, typeof CapabilitySetSchema>
);

export const AgentMediaCapabilitiesSchema = z.object({
	aiAssistant: ActorCapabilitiesSchema,
	systemAgent: ActorCapabilitiesSchema,
});
export type AgentMediaCapabilities = z.infer<
	typeof AgentMediaCapabilitiesSchema
>;

function enabledActorCapabilities() {
	return Object.fromEntries(
		AGENT_MEDIA_KINDS.map((kind) => [
			kind,
			{ receive: true, read: true, send: true },
		])
	) as AgentMediaCapabilities[AgentMediaActor];
}

export function createDefaultAgentMediaCapabilities(): AgentMediaCapabilities {
	return {
		aiAssistant: enabledActorCapabilities(),
		systemAgent: enabledActorCapabilities(),
	};
}

export const DEFAULT_AGENT_MEDIA_CAPABILITIES =
	createDefaultAgentMediaCapabilities();

/** Plan ceiling; all current plans intentionally begin fully enabled. */
export const PLAN_AGENT_MEDIA_CAPABILITIES: Record<
	OrgPlan,
	AgentMediaCapabilities
> = {
	chat: createDefaultAgentMediaCapabilities(),
	standard: createDefaultAgentMediaCapabilities(),
	premium: createDefaultAgentMediaCapabilities(),
};

export function classifyAgentMediaType(mediaType: string): AgentMediaKind {
	const normalized = mediaType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
	if (normalized.startsWith("image/")) {
		return "image";
	}
	if (normalized.startsWith("audio/")) {
		return "audio";
	}
	if (normalized.startsWith("video/")) {
		return "video";
	}
	if (normalized === "application/pdf" || normalized === "application/x-pdf") {
		return "pdf";
	}
	// Everything else — spreadsheets, Word/PowerPoint/OpenDocument, plain text,
	// CSV, JSON, and any unrecognized file — belongs to the merged "Documents &
	// files" family.
	return "document";
}

/**
 * Whether a media type is UTF-8 text the platform can decode locally and hand
 * to the model as text (as opposed to a binary document with no parser). Used
 * by the media pipeline: a text-like {@link AgentMediaKind} of `document`
 * inlines its decoded contents, while a binary `document` is tombstoned.
 */
export function isTextLikeAgentMediaType(mediaType: string): boolean {
	const normalized = mediaType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
	return (
		normalized.startsWith("text/") ||
		normalized === "application/json" ||
		normalized.endsWith("+json") ||
		normalized === "application/xml" ||
		normalized.endsWith("+xml")
	);
}

/**
 * A capability payload as it may actually arrive at runtime, rather than as the
 * schema promises. Stored rows and shipped bundles disagree about the kind set
 * across a deploy: a browser running a pre-merge bundle asks for a kind the
 * server now strips (`other`, `spreadsheet`, `text`), and a plan value outside
 * {@link PLAN_AGENT_MEDIA_CAPABILITIES} has no ceiling entry at all.
 */
type PartialAgentMediaCapabilities = {
	[actor in AgentMediaActor]?: {
		[kind in AgentMediaKind]?: Partial<Record<AgentMediaAction, boolean>>;
	};
};

/**
 * Total capability lookup: an absent actor, kind, or action fails closed. These
 * indexes used to be unguarded, so a payload missing a kind threw and — since
 * the policy is read from a sidebar provider during render — took the whole app
 * down. The server remains the authoritative gate, so denying is the safe miss.
 */
function capabilityAllowed(
	capabilities: PartialAgentMediaCapabilities | undefined,
	actor: AgentMediaActor,
	kind: AgentMediaKind,
	action: AgentMediaAction
): boolean {
	return capabilities?.[actor]?.[kind]?.[action] === true;
}

export function isAgentMediaAllowed(
	plan: OrgPlan,
	settings: AgentMediaCapabilities,
	actor: AgentMediaActor,
	mediaType: string,
	action: AgentMediaAction
): boolean {
	return isAgentMediaAllowedByPolicies(
		PLAN_AGENT_MEDIA_CAPABILITIES[plan],
		settings,
		actor,
		mediaType,
		action
	);
}

/** Pure policy intersection used by plan editors and focused tests. */
export function isAgentMediaAllowedByPolicies(
	planSettings: AgentMediaCapabilities,
	settings: AgentMediaCapabilities,
	actor: AgentMediaActor,
	mediaType: string,
	action: AgentMediaAction
): boolean {
	const kind = classifyAgentMediaType(mediaType);
	return (
		capabilityAllowed(planSettings, actor, kind, action) &&
		capabilityAllowed(settings, actor, kind, action)
	);
}

const ACCEPT_TOKENS_BY_KIND: Record<AgentMediaKind, readonly string[]> = {
	image: ["image/*"],
	audio: ["audio/*"],
	video: ["video/*"],
	pdf: ["application/pdf"],
	// The merged "Documents & files" family also covers arbitrary/unknown files
	// (the former "Other files" kind), so it is treated as the picker catch-all.
	document: [
		"text/*",
		"application/json",
		"application/xml",
		"application/vnd.ms-excel",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		"application/vnd.oasis.opendocument.spreadsheet",
		"application/msword",
		"application/vnd.ms-powerpoint",
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
		"application/vnd.openxmlformats-officedocument.presentationml.presentation",
		"application/vnd.oasis.opendocument.text",
		"application/vnd.oasis.opendocument.presentation",
	],
};

/** Browser-picker hints derived from the effective receive policy. */
export function resolveAgentMediaReceivePolicy(
	plan: OrgPlan,
	settings: AgentMediaCapabilities,
	actor: AgentMediaActor
): {
	acceptString: string | undefined;
	acceptsAnyFile: boolean;
	acceptsAudio: boolean;
} {
	const accepts = (kind: AgentMediaKind) =>
		capabilityAllowed(
			PLAN_AGENT_MEDIA_CAPABILITIES[plan],
			actor,
			kind,
			"receive"
		) && capabilityAllowed(settings, actor, kind, "receive");
	// "Documents & files" absorbs the former catch-all "other" kind, so enabling
	// it means the picker accepts any file (no `accept` filter).
	const acceptsAnyFile = accepts("document");
	return {
		// No `accept` attribute means any file. The server remains authoritative.
		acceptString: acceptsAnyFile
			? undefined
			: AGENT_MEDIA_KINDS.filter(accepts)
					.flatMap((kind) => ACCEPT_TOKENS_BY_KIND[kind])
					.join(","),
		acceptsAnyFile:
			acceptsAnyFile || AGENT_MEDIA_KINDS.some((kind) => accepts(kind)),
		acceptsAudio: accepts("audio"),
	};
}
